import React, { useCallback, useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import { useSearchParams } from 'react-router-dom'

import { UsersIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

import { Text } from '../../ui'

import { useAuthContext } from '../../../contexts/AuthContext'

import { socialService, SOCIAL_POST_QUERY_PARAM } from '../../../services/social'

import { SocialPost } from '../../../types/social'

import { DashboardCardSkeleton } from '../DashboardSkeleton'

import PostCard from './PostCard'

import PostComposer from './PostComposer'

import PostDetailModal from './PostDetailModal'

import PostList from './PostList'

import TopPostsPanel from './TopPostsPanel'

import TopActiveUsersPanel from './TopActiveUsersPanel'

import { feedCardClass } from './socialUtils'



const AUTO_REFRESH_INTERVAL_MS = 30_000

const AUTO_REFRESH_TICK_MS = 200



export interface SocialFeedToolbarState {
  refreshing: boolean
  autoRefreshSeconds: number
  postsCount: number
  onRefresh: () => void
}

export interface SocialFeedProps {
  moderation?: boolean
  /** Esconde o botão Atualizar interno (ex.: quando é renderizado junto às abas da home). */
  hideRefreshButton?: boolean
  onToolbarChange?: (state: SocialFeedToolbarState | null) => void
  /** Prefill do compositor (ex.: resultado do caça-palavras). */
  composerPrefill?: string | null
  composerPrefillKey?: number
}

const SocialFeed: React.FC<SocialFeedProps> = ({
  moderation = false,
  hideRefreshButton = false,
  onToolbarChange,
  composerPrefill,
  composerPrefillKey,
}) => {

  const { user, profile } = useAuthContext()

  const [searchParams, setSearchParams] = useSearchParams()

  const [posts, setPosts] = useState<SocialPost[]>([])

  const [loading, setLoading] = useState(true)

  const [error, setError] = useState<string | null>(null)

  const [refreshing, setRefreshing] = useState(false)

  const [modalPost, setModalPost] = useState<SocialPost | null>(null)

  const [modalLoading, setModalLoading] = useState(false)

  const [modalError, setModalError] = useState<string | null>(null)

  const [expandedCommentsPostId, setExpandedCommentsPostId] = useState<string | null>(null)

  const [autoRefreshProgress, setAutoRefreshProgress] = useState(0)

  const autoRefreshStartRef = useRef(Date.now())

  const refreshingRef = useRef(false)



  const deepLinkPostId = searchParams.get(SOCIAL_POST_QUERY_PARAM)



  const loadPosts = useCallback(async (silent = false) => {

    try {

      if (silent) setRefreshing(true)

      else setLoading(true)

      setError(null)

      const data = await socialService.listPosts()

      setPosts(data)

    } catch (err) {

      setError(err instanceof Error ? err.message : 'Erro ao carregar feed')

    } finally {

      setLoading(false)

      setRefreshing(false)

    }

  }, [])



  refreshingRef.current = refreshing



  const resetAutoRefreshTimer = useCallback(() => {

    autoRefreshStartRef.current = Date.now()

    setAutoRefreshProgress(0)

  }, [])



  const handleRefresh = useCallback(() => {

    resetAutoRefreshTimer()

    void loadPosts(true)

  }, [loadPosts, resetAutoRefreshTimer])

  useEffect(() => {
    if (!onToolbarChange || moderation) return

    const autoRefreshSeconds = Math.max(
      0,
      Math.ceil(((100 - autoRefreshProgress) / 100) * (AUTO_REFRESH_INTERVAL_MS / 1000))
    )

    onToolbarChange({
      refreshing,
      autoRefreshSeconds,
      postsCount: posts.length,
      onRefresh: handleRefresh,
    })

    return () => onToolbarChange(null)
  }, [onToolbarChange, moderation, refreshing, autoRefreshProgress, handleRefresh, posts.length])



  useEffect(() => {

    if (moderation) return



    const tick = () => {

      const elapsed = Date.now() - autoRefreshStartRef.current

      const progress = Math.min((elapsed / AUTO_REFRESH_INTERVAL_MS) * 100, 100)

      setAutoRefreshProgress(progress)



      if (elapsed < AUTO_REFRESH_INTERVAL_MS) return



      resetAutoRefreshTimer()

      if (!refreshingRef.current) {

        void loadPosts(true)

      }

    }



    const intervalId = window.setInterval(tick, AUTO_REFRESH_TICK_MS)

    return () => window.clearInterval(intervalId)

  }, [moderation, loadPosts, resetAutoRefreshTimer])



  useEffect(() => {

    loadPosts()

  }, [loadPosts])



  const clearDeepLinkParam = useCallback(() => {

    if (!searchParams.has(SOCIAL_POST_QUERY_PARAM)) return

    const next = new URLSearchParams(searchParams)

    next.delete(SOCIAL_POST_QUERY_PARAM)

    setSearchParams(next, { replace: true })

  }, [searchParams, setSearchParams])



  const closeModal = useCallback(() => {

    setModalPost(null)

    setModalError(null)

    clearDeepLinkParam()

  }, [clearDeepLinkParam])



  useEffect(() => {

    if (!deepLinkPostId || moderation) return



    let cancelled = false



    const openFromDeepLink = async () => {

      const local = posts.find((p) => p.id === deepLinkPostId)

      if (local) {

        setModalPost(local)

        setModalError(null)

        return

      }



      setModalLoading(true)

      setModalError(null)

      try {

        const fetched = await socialService.getPostById(deepLinkPostId)

        if (cancelled) return

        if (!fetched) {

          setModalPost(null)

          setModalError('Esta postagem não existe ou foi excluída.')

          return

        }

        setModalPost(fetched)

      } catch (err) {

        if (cancelled) return

        setModalPost(null)

        setModalError(err instanceof Error ? err.message : 'Erro ao carregar postagem')

      } finally {

        if (!cancelled) setModalLoading(false)

      }

    }



    void openFromDeepLink()



    return () => {

      cancelled = true

    }

  }, [deepLinkPostId, moderation, posts])



  const syncPostInState = useCallback((postId: string, updater: (post: SocialPost) => SocialPost) => {

    setPosts((prev) => prev.map((p) => (p.id === postId ? updater(p) : p)))

    setModalPost((prev) => (prev?.id === postId ? updater(prev) : prev))

  }, [])



  const handleCreatePost = async (content: string, files?: File[]) => {

    const created = await socialService.createPost(content, files)

    setPosts((prev) => [created, ...prev])

  }



  const handleUpdatePost = async (postId: string, content: string) => {

    const updated = await socialService.updatePost(postId, content)

    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, ...updated } : p)))

  }



  const handleDeletePost = async (postId: string) => {

    await socialService.deletePost(postId)

    setPosts((prev) => prev.filter((p) => p.id !== postId))

    if (modalPost?.id === postId) closeModal()

  }



  const handleReact = async (postId: string, emoji: string) => {

    const userId = user?.id

    if (!userId) return



    const result = await socialService.setReaction(postId, emoji)

    const updater = (p: SocialPost) => {

      const reactions = (p.reactions || []).filter((r) => r.user_id !== userId)

      if (result) {

        reactions.push({

          ...result,

          user: profile

            ? { id: profile.id, name: profile.name, avatar_url: profile.avatar_url, department: profile.department }

            : result.user,

        })

      }

      return { ...p, reactions }

    }

    syncPostInState(postId, updater)

  }



  const handleAddComment = async (postId: string, content: string) => {

    const comment = await socialService.addComment(postId, content)

    syncPostInState(postId, (p) => ({

      ...p,

      comments: [...(p.comments || []), comment],

      comment_count: (p.comment_count || 0) + 1,

    }))

  }



  const handleUpdateComment = async (commentId: string, content: string) => {

    const updated = await socialService.updateComment(commentId, content)

    setPosts((prev) =>

      prev.map((p) => ({

        ...p,

        comments: (p.comments || []).map((c) =>

          c.id === commentId ? { ...c, ...updated } : c

        ),

      }))

    )

    setModalPost((prev) =>

      prev

        ? {

            ...prev,

            comments: (prev.comments || []).map((c) =>

              c.id === commentId ? { ...c, ...updated } : c

            ),

          }

        : prev

    )

  }



  const handleDeleteComment = async (commentId: string) => {

    await socialService.deleteComment(commentId)

    setPosts((prev) =>

      prev.map((p) => ({

        ...p,

        comments: (p.comments || []).filter((c) => c.id !== commentId),

        comment_count: Math.max(0, (p.comment_count || 0) - 1),

      }))

    )

    setModalPost((prev) =>

      prev

        ? {

            ...prev,

            comments: (prev.comments || []).filter((c) => c.id !== commentId),

            comment_count: Math.max(0, (prev.comment_count || 0) - 1),

          }

        : prev

    )

  }



  const scrollToPost = useCallback((postId: string) => {

    const el = document.getElementById(`social-post-${postId}`)

    if (!el) return

    el.scrollIntoView({ behavior: 'smooth', block: 'start' })

    el.classList.add('ring-2', 'ring-primary-500/60', 'ring-offset-2', 'dark:ring-offset-gray-900')

    window.setTimeout(() => {

      el.classList.remove('ring-2', 'ring-primary-500/60', 'ring-offset-2', 'dark:ring-offset-gray-900')

    }, 2000)

  }, [])



  if (moderation) {

    return (

      <section className="mb-0">

        <PostList

          posts={posts}

          loading={loading}

          error={error}

          refreshing={refreshing}

          onRefresh={() => loadPosts(true)}

          onDelete={handleDeletePost}

          onDeleteComment={handleDeleteComment}

        />

      </section>

    )

  }



  return (

    <section className="mb-0">

      {!hideRefreshButton && (
      <div className="mb-3 flex justify-end">

          <button

            type="button"

            onClick={handleRefresh}

            disabled={refreshing}

            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 transition-colors"

          >

            <ArrowPathIcon className={clsx('h-4 w-4', refreshing && 'animate-spin')} />

            {refreshing ? (

              'Atualizando…'

            ) : (

              <>

                Atualizar

                <span className="tabular-nums text-[10px] text-gray-500 dark:text-gray-400 min-w-[1.35rem] text-right">

                  {Math.max(0, Math.ceil(((100 - autoRefreshProgress) / 100) * (AUTO_REFRESH_INTERVAL_MS / 1000)))}s

                </span>

              </>

            )}

          </button>

      </div>
      )}



      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

        <div className="lg:col-span-8 space-y-4 min-w-0">

          <PostComposer
            onSubmit={handleCreatePost}
            initialContent={composerPrefill ?? undefined}
            initialContentKey={composerPrefillKey}
          />



          {loading && (

            <div className="space-y-4">

              <DashboardCardSkeleton className={clsx(feedCardClass, 'h-36')} />

              <DashboardCardSkeleton className={clsx(feedCardClass, 'h-48')} />

            </div>

          )}



          {error && !loading && (

            <div className={clsx(feedCardClass, 'p-4 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20')}>

              <Text size="sm" className="text-yellow-800 dark:text-yellow-200">{error}</Text>

            </div>

          )}



          {!loading && !error && posts.length === 0 && (

            <div className={clsx(feedCardClass, 'p-8 text-center')}>

              <UsersIcon className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />

              <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">

                Seu feed está vazio

              </Text>

              <Text size="xs" color="muted" className="mt-1">

                Seja o primeiro a compartilhar algo com a equipe!

              </Text>

            </div>

          )}



          {!loading && !error && posts.length > 0 && (

            <div className="space-y-4">

              {posts.map((post) => (

                <div

                  key={post.id}

                  id={`social-post-${post.id}`}

                  className="scroll-mt-24 rounded-xl transition-shadow duration-300"

                >

                  <PostCard

                    post={post}

                    currentUserId={user?.id}

                    currentUserName={profile?.name}

                    currentUserAvatar={profile?.avatar_url}

                    onUpdate={handleUpdatePost}

                    onDelete={handleDeletePost}

                    onReact={handleReact}

                    onAddComment={handleAddComment}

                    onUpdateComment={handleUpdateComment}

                    onDeleteComment={handleDeleteComment}

                    commentsOpen={expandedCommentsPostId === post.id}

                    onCommentsOpenChange={(open) => {

                      setExpandedCommentsPostId(open ? post.id : null)

                    }}

                  />

                </div>

              ))}

            </div>

          )}

        </div>



        <div className="lg:col-span-4 min-w-0 space-y-4 lg:sticky lg:top-4">

          <TopPostsPanel

            posts={posts}

            loading={loading}

            onSelectPost={scrollToPost}

          />

          <TopActiveUsersPanel

            posts={posts}

            loading={loading}

          />

        </div>

      </div>



      {modalLoading && deepLinkPostId && (

        <div className={clsx(feedCardClass, 'mt-4 p-4 border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20')}>

          <Text size="sm" className="text-primary-800 dark:text-primary-200">Carregando postagem…</Text>

        </div>

      )}



      {modalError && deepLinkPostId && !modalPost && (

        <div className={clsx(feedCardClass, 'mt-4 p-4 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-between gap-3')}>

          <Text size="sm" className="text-yellow-800 dark:text-yellow-200">{modalError}</Text>

          <button

            type="button"

            onClick={closeModal}

            className="text-xs font-medium text-yellow-700 dark:text-yellow-300 hover:underline shrink-0"

          >

            Fechar

          </button>

        </div>

      )}



      {modalPost && (

        <PostDetailModal

          post={modalPost}

          interactive

          currentUserId={user?.id}

          currentUserName={profile?.name}

          currentUserAvatar={profile?.avatar_url}

          onClose={closeModal}

          onAddComment={(content) => handleAddComment(modalPost.id, content)}

          onUpdateComment={handleUpdateComment}

          onDeleteComment={handleDeleteComment}

          onReact={(emoji) => handleReact(modalPost.id, emoji)}

        />

      )}

    </section>

  )

}



export default SocialFeed

